// CLI v0.8 — `cloudgrid secrets list [<name>]`. Spec v17 §13.9.
//
// Server returns key metadata only. The CLI never prints (or even receives)
// values — that property holds end-to-end and is enforced by the server's
// `GET /api/v2/runtimes/:id/secrets` shape (`{ secrets: [{ key }] }`).

import { Command } from 'commander';
import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { resolveEntityTarget } from '../../services/resolve-entity-target.js';

interface SecretsListResponse { secrets: Array<{ key: string }> }

export interface RunSecretsListOpts {
  client: IV2Client;
  name: string | undefined;
  json?: boolean;
  output?: string;
  cwd?: string;
}

export async function runSecretsList(opts: RunSecretsListOpts): Promise<void> {
  const target = await resolveEntityTarget(opts.client, opts.name, opts.cwd);
  const resp = await opts.client.get<SecretsListResponse>(
    `/api/v2/runtimes/${encodeURIComponent(target.entity_id)}/secrets`,
  );
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  if (out.isStructured) {
    out.json(() => resp.secrets);
    return;
  }
  if (resp.secrets.length === 0) {
    process.stdout.write(`No secrets set on ${target.name}.\n`);
    return;
  }
  for (const { key } of resp.secrets) {
    process.stdout.write(`${key}\n`);
  }
}

export const secretsListCommand: CommanderCommand = addOutputOption(
  new Command('list')
    .description('List secret keys for a runtime (values never leave the cluster)')
    .argument('[name]', 'Entity slug or id (defaults to the entity linked in this directory)')
    .option('--json', 'JSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: { json?: boolean; output?: string }) => {
  try {
    const client = await getV2Client();
    await runSecretsList({ client, name, json: opts.json, output: opts.output });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
